import { useState } from 'react';
import { get, type FieldPath, type FieldValues, type UseFormReturn } from 'react-hook-form';

export interface UseEditModalOptions<T extends FieldValues> {
  form: Pick<
    UseFormReturn<T, unknown, unknown>,
    'getValues' | 'setValue' | 'trigger' | 'clearErrors' | 'formState'
  >;
  /** The fields this modal edits. Cancel puts them back; a failed save on one opens it. */
  fields: ReadonlyArray<FieldPath<T>>;
}

export interface EditModalControls {
  open: boolean;
  /** Opens the modal and remembers the fields, so Cancel can put them back. */
  show: () => void;
  /** Validates this modal's fields and closes; the values stay on the page for the save bar. */
  done: () => Promise<void>;
  /** Spread on `EditModal`. */
  modal: { open: boolean; onOpenChange: (open: boolean) => void; onDone: () => void };
}

/**
 * An edit modal over fields of the page's form. The fields stay in that form, so the save bar sees
 * them while the modal is open; Cancel restores what they held when it opened. A save that fails
 * on one of the fields opens the modal, so no error stays hidden behind a summary.
 */
export function useEditModal<T extends FieldValues>({
  form,
  fields,
}: UseEditModalOptions<T>): EditModalControls {
  const [open, setOpen] = useState(false);
  const [snapshot, setSnapshot] = useState<unknown[]>([]);
  // A copy: react-hook-form edits nested values such as `seo.title` in place.
  const remember = () => fields.map((field) => structuredClone(form.getValues(field)));
  const saved = () =>
    fields.map((field) => structuredClone(get(form.formState.defaultValues, field) as unknown));

  const hasError = fields.some((field) => get(form.formState.errors, field) !== undefined);
  const [openedForError, setOpenedForError] = useState(false);
  if (hasError !== openedForError) {
    setOpenedForError(hasError);
    if (hasError && !open) {
      // Opened by a refused save: Cancel goes back to what's saved, not to the refused value.
      setSnapshot(saved());
      setOpen(true);
    }
  }

  const show = () => {
    setSnapshot(remember());
    setOpen(true);
  };
  const cancel = () => {
    fields.forEach((field, index) => {
      // setValue's types want the exact field type; the snapshot holds what getValues returned.
      form.setValue(field, snapshot[index] as never, { shouldDirty: true });
    });
    form.clearErrors(fields);
    setOpen(false);
  };
  const done = async () => {
    if (await form.trigger(fields)) setOpen(false);
  };

  return {
    open,
    show,
    done,
    modal: {
      open,
      onOpenChange: (next) => {
        if (next) show();
        else cancel();
      },
      onDone: () => void done(),
    },
  };
}
